import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { MedicusLogo } from '@components/MedicusLogo';
import { PlanMockupCard } from '@components/onboarding/PlanMockupCard';
import { TyCMs2Modal } from '@components/onboarding/TyCMs2Modal';
import { aceptarOfertaYPagar, getOferta, type OfertaPublica } from '@lib/services/oferta.service';

function fmtMoney(n: number): string {
  // Sin decimales, separador de miles es-AR y sin espacio ("$123.456"),
  // igual que el mail de oferta.
  return '$' + Math.round(n).toLocaleString('es-AR');
}

function primerNombre(nombres: string): string {
  const t = nombres.trim().split(/\s+/)[0] ?? '';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

function fmtVence(expiraAt: string): string {
  return new Date(expiraAt).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const NAVY = '#002564';
const GRAY = '#7f92b2';
const BLUE = '#6399f8';

/**
 * Beneficios destacados del MS2 en la oferta — los mismos 5 del diseño de
 * Figma (MS2-MAILING) y del mail. Las palabras entre `**...**` se renderean
 * en negrita (ver `renderBolds`).
 */
const BENEFICIOS_MS2 = [
  'Consultas virtuales de todas las especialidades **sin copago**',
  '**Habitación individual** en sanatorios de primer nivel',
  'Medicamentos ambulatorios **con descuento**',
  'Salud mental, kinesiología y odontología',
  'Estudios y diagnósticos',
];

/** Parser mini de bold (`**palabra**`) para los beneficios. */
function renderBolds(texto: string): ReactNode {
  return texto.split(/(\*\*[^*]+\*\*)/g).map((parte, i) =>
    parte.startsWith('**') && parte.endsWith('**') ? (
      <strong key={i}>{parte.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{parte}</span>
    ),
  );
}

/**
 * Credencial del Plan MS2: la MISMA card del paso de selección de plan del
 * wizard (`PlanMockupCard variant="verde"`, con la interacción de tilt/brillo
 * `onb-card-tilt`), con una etiqueta "Plan MS2" como en el Figma. Le sumamos
 * una entrada animada (fade + slide-up + scale) con framer-motion. Respeta
 * `prefers-reduced-motion`.
 */
function CredencialAnimada() {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 28, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1], delay: 0.08 }}
      style={{ maxWidth: 360, width: '100%', margin: '0 auto' }}
    >
      <PlanMockupCard variant="verde" label="Plan MS2" />
    </motion.div>
  );
}

export function OfertaPage() {
  const { token = '' } = useParams<{ token: string }>();
  const q = useQuery({
    queryKey: ['oferta-publica', token],
    queryFn: () => getOferta(token),
    enabled: token.length > 0,
    retry: false,
  });

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf3 100%)',
        padding: '32px 16px',
      }}
    >
      <div
        style={{
          maxWidth: 620,
          margin: '0 auto',
          background: 'white',
          borderRadius: 16,
          padding: 'clamp(24px, 4vw, 40px)',
          boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
        }}
      >
        <header style={{ marginBottom: 24, textAlign: 'center' }}>
          <MedicusLogo width={120} />
        </header>

        {q.isLoading && (
          <p className="text-element-mid" style={{ textAlign: 'center' }}>
            Cargando…
          </p>
        )}
        {q.isError && <OfertaError error={q.error} />}
        {q.data && <OfertaFlujo oferta={q.data} token={token} />}
      </div>
    </div>
  );
}

/**
 * Replica el flujo del wizard pero acotado al MS2: muestra SOLO el plan
 * superador (con el precio nuevo), exige aceptar los T&C en el modal con scroll
 * obligatorio (`TyCMs2Modal`), dispara el pago y muestra la pantalla final.
 */
function OfertaFlujo({ oferta, token }: { oferta: OfertaPublica; token: string }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [fase, setFase] = useState<'plan' | 'final'>('plan');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => aceptarOfertaYPagar(token),
    onSuccess: (data) => {
      setModalOpen(false);
      if (data.urlCheckout) {
        // Igual que PagoStep del wizard: abrimos el checkout en otra pestaña y
        // mostramos la pantalla de información (no esperamos al gateway).
        window.open(data.urlCheckout, '_blank', 'noopener,noreferrer');
        setFase('final');
      } else {
        setError(data.motivoRechazo ?? 'No pudimos abrir el pago. Probá de nuevo en unos minutos.');
      }
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } } };
      setModalOpen(false);
      setError(
        e.response?.data?.error?.message ??
          e.response?.data?.message ??
          'No pudimos iniciar el pago. Reintentá en unos minutos.',
      );
    },
  });

  if (fase === 'final') return <OfertaFinal />;

  return (
    <>
      <h2
        style={{
          color: NAVY,
          textAlign: 'center',
          margin: '0 0 12px',
          fontSize: 'clamp(22px, 5.5vw, 26px)',
          fontWeight: 800,
          letterSpacing: '-0.5px',
        }}
      >
        Hola, {primerNombre(oferta.titular.nombres) || 'titular'}
      </h2>
      <p
        style={{
          color: GRAY,
          textAlign: 'center',
          lineHeight: 1.4,
          margin: '0 0 28px',
          fontSize: 'clamp(15px, 3.8vw, 18px)',
          fontWeight: 500,
        }}
      >
        Esta es tu oferta para acceder al <strong style={{ color: NAVY }}>Plan MS2</strong>, el plan
        superador.
      </p>

      <div style={{ marginBottom: 24 }}>
        <CredencialAnimada />
      </div>

      <div
        style={{
          background: '#f2f4f8',
          borderRadius: 14,
          padding: 'clamp(22px, 6vw, 30px) 20px',
          textAlign: 'center',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 'clamp(36px, 11vw, 46px)',
            fontWeight: 800,
            color: NAVY,
            letterSpacing: '-1px',
            lineHeight: 1,
          }}
        >
          {fmtMoney(oferta.montoMs2)}
          <span style={{ fontSize: '0.5em', fontWeight: 700, verticalAlign: 'super' }}>*</span>
        </span>
      </div>
      <p style={{ fontSize: 12.5, color: GRAY, textAlign: 'center', margin: '0 0 24px' }}>
        * Valor mensual{' '}
        {oferta.cantidadIntegrantes === 1
          ? 'para vos'
          : `del grupo de ${oferta.cantidadIntegrantes} integrantes`}
        , sin IVA.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {BENEFICIOS_MS2.map((b) => (
          <div
            key={b}
            style={{
              alignSelf: 'flex-start',
              maxWidth: '100%',
              display: 'inline-flex',
              gap: 10,
              alignItems: 'flex-start',
              background: '#f1f6fe',
              borderRadius: 18,
              padding: '9px 16px 9px 9px',
            }}
          >
            <span
              style={{
                width: 24,
                height: 24,
                minWidth: 24,
                borderRadius: '50%',
                background: BLUE,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Check size={14} strokeWidth={3} />
            </span>
            <span style={{ fontSize: 14.5, lineHeight: 1.4, color: NAVY }}>{renderBolds(b)}</span>
          </div>
        ))}
      </div>

      {error && (
        <p
          className="status status-error"
          style={{ display: 'block', marginBottom: 16, textAlign: 'center' }}
        >
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() => {
          setError(null);
          setModalOpen(true);
        }}
        style={{
          width: '100%',
          padding: '16px 24px',
          borderRadius: 800,
          border: 'none',
          background: BLUE,
          color: 'white',
          fontSize: 16,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          boxShadow: 'inset 0 2px 0 0 rgba(255,255,255,0.12)',
          cursor: mutation.isPending ? 'not-allowed' : 'pointer',
          opacity: mutation.isPending ? 0.6 : 1,
        }}
      >
        Aceptar oferta y continuar
      </button>

      <p
        style={{
          color: GRAY,
          fontSize: 12.5,
          textAlign: 'center',
          marginTop: 14,
          marginBottom: 0,
          lineHeight: 1.5,
        }}
      >
        Vas a leer y aceptar los términos y condiciones antes de pagar. Esta oferta vence el{' '}
        <strong style={{ color: NAVY }}>{fmtVence(oferta.expiraAt)}</strong>.
      </p>

      <TyCMs2Modal
        open={modalOpen}
        submitting={mutation.isPending}
        onCancelar={() => setModalOpen(false)}
        onAceptar={() => {
          setError(null);
          mutation.mutate();
        }}
      />
    </>
  );
}

function OfertaFinal() {
  return (
    <div style={{ textAlign: 'center', padding: '12px 0' }}>
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: '#e7f5e9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}
      >
        <Check size={40} style={{ color: '#2f7d32' }} strokeWidth={2.5} />
      </div>
      <h2 style={{ color: '#002564', margin: '0 0 10px', fontSize: 24 }}>¡Listo!</h2>
      <p style={{ color: '#334155', lineHeight: 1.55, margin: '0 auto', maxWidth: 430 }}>
        Abrimos el pago en otra pestaña. Completá los datos ahí para confirmar tu adhesión al{' '}
        <strong>Plan MS2</strong>. Cuando se acredite el pago te llega la confirmación por correo.
      </p>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 18, marginBottom: 0 }}>
        Si la pestaña de pago no se abrió, revisá que el navegador no la haya bloqueado o pedile a tu
        asesor un nuevo link.
      </p>
    </div>
  );
}

function OfertaError({ error }: { error: unknown }) {
  const e = error as {
    response?: {
      status?: number;
      data?: { code?: string; message?: string; error?: { code?: string; message?: string } };
    };
  };
  const status = e.response?.status;
  const code = e.response?.data?.code ?? e.response?.data?.error?.code;
  const msg =
    e.response?.data?.message ??
    e.response?.data?.error?.message ??
    'El link que recibiste no es válido o ya no está disponible.';

  let titulo = 'Oferta no disponible';
  if (status === 403 && code === 'OFERTA_EXPIRADA') titulo = 'La oferta venció';
  if (status === 403 && code === 'PAGO_YA_APROBADO') titulo = 'Tu pago ya está confirmado';

  return (
    <>
      <h2 style={{ color: '#002564', margin: '0 0 12px', fontSize: 22, textAlign: 'center' }}>
        {titulo}
      </h2>
      <p style={{ color: '#475569', textAlign: 'center', lineHeight: 1.55, margin: 0 }}>{msg}</p>
      <p
        style={{
          color: '#64748b',
          fontSize: 13,
          textAlign: 'center',
          marginTop: 20,
          marginBottom: 0,
        }}
      >
        Si tenés dudas, contactá al asesor que te llamó.
      </p>
    </>
  );
}
